import { Wizard } from '@/components/layout/Wizard';
import { Background, Button, Container, ContentArea, ContentHeader, IconFa, WizardContainer } from '@/components/wm';
import { cn } from '@/utils';
import 'gridstack/dist/gridstack.min.css';
import { apiRequest } from '@/utils/request';
import { GridStack } from 'gridstack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NewWidgetType } from './Content/NewWidgetType';
import { Widget } from './Content/Widget';
import { buildWidget, DEFAULT_WIDGET_TYPE, stepsForType, widgetTypes } from './widgetTypes';

// Around the edit and delete buttons in edit mode, so they stand out whatever
// the colour of the widget beneath them.
const EDIT_BUTTON_BORDER = 'tw-border-2 tw-border-solid tw-border-white';

export default function CustomDashboard() {
    const
        initialWidgets = WiseMetering.currentOrganization.get('dashboard')?.widgets || [],
        organizationSubdomain = WiseMetering.currentOrganization.get('subdomain');

    const
        [isEditing, setIsEditing] = useState(false),
        [newWidgetType, setNewWidgetType] = useState(DEFAULT_WIDGET_TYPE),
        [plusWidgetAdded, setPlusWidgetAdded] = useState(false),
        [savedWidgets, setSavedWidgets] = useState(initialWidgets),
        [widgets, setWidgets] = useState(initialWidgets),
        // The wizard, when open: on a new widget, or on an existing one to edit
        // (`editingId`).
        [wizard, setWizard] = useState(null);

    const
        gridRef = useRef(null),
        grid = useRef(null);

    // The steps that follow the type picker come from whichever type is selected
    // inside the wizard.
    // An edit leaves out the type picker: a widget keeps its type.
    const steps = [
        ...(wizard?.editingId
            ? []
            : [{ Component: NewWidgetType, title: i18next.t('dashboard.custom_dashboard.type_title'), description: i18next.t('dashboard.custom_dashboard.type_description') }]),
        ...stepsForType(newWidgetType)
    ];

    const onWizardDataChange = useCallback(data => setNewWidgetType(data.type), []);

    const addNewWidget = () => {
        setNewWidgetType(DEFAULT_WIDGET_TYPE);
        setWizard({ editingId: null, widget: buildWidget(DEFAULT_WIDGET_TYPE) });
    };

    const closeWizard = () => setWizard(null);

    // The wizard's copy gets its own id: its preview would otherwise share a DOM
    // id with the widget on the dashboard, which the value card looks itself up by.
    const editWidget = widget => {
        setNewWidgetType(widget.type);
        setWizard({ editingId: widget.id, widget: { ...widget, id: `editing-${widget.id}` } });
    };

    // Where the widget sits and its size stay as they are on the grid.
    const updateWidget = (id, data) => {
        const { position, size } = widgets.find(w => w.id === id).graphical;

        setWidgets(prev => prev.map(w => (w.id === id
            ? { ...data, id, graphical: { ...data.graphical, position, size } }
            : w)));
        setWizard(null);
    };

    const cancelEditing = () => {
        if (!grid.current) {
            return;
        }

        // Clean gridstack and React state to restore previous layout
        grid.current.off('change');
        grid.current.destroy(false);
        grid.current = null;

        setWidgets([]);
        setPlusWidgetAdded(false);

        // Rebuild GridStack
        setTimeout(() => {
            grid.current = GridStack.init(
                {
                    column: 12,
                    cellHeight: 100,
                    float: false,
                    resizable: { handles: 'all' },
                    draggable: { handle: '.widget-header' }
                },
                gridRef.current
            );

            setWidgets(savedWidgets);

            grid.current.setStatic(true);
            grid.current.enableMove(false);
            grid.current.enableResize(false);

            setIsEditing(false);
        }, 0);
    };

    const handleSubmit = async data => {
        if (wizard?.editingId) {
            updateWidget(wizard.editingId, data);
            return;
        }

        const nextY = Math.max(0, ...widgets.map(w => w.graphical.position.y + w.graphical.size.h));

        const newWidget = {
            ...data,
            id: `${Date.now()}-${organizationSubdomain}`,
            graphical: {
                ...data.graphical,
                position: { x: 0, y: nextY },
                // The footprint comes from the widget type's defaults, so a chart
                // is not created at the size of a value card.
                size: { ...data.graphical.size }
            }
        };

        const newWidgets = [...widgets, newWidget];

        setWidgets(newWidgets);
        setWizard(null);

        // Allow GridStack to process the new widget
        setTimeout(() => {
            grid.current?.makeWidget(
                gridRef.current.querySelector(`[gs-id="${newWidget.id}"]`)
            );
        }, 0);
    };

    const removeWidget = id => {
        setWidgets(prev =>
            prev.map(w => w.id === id ? { ...w, deleted: true } : w)
        );
    };

    const saveDashboard = async () => {
        const submitData = {
            dashboard: {
                widgets: widgets.filter(w => !w.deleted)
            }
        };

        try {
            const payload = submitData;
            const url = `/organizations/${WiseMetering.currentOrganization.get('id')}/actions/update_dashboard`;
            const response = await apiRequest(url, 'PUT', payload);

            if (response.data.dashboard) {
                setWidgets(response.data.dashboard.widgets);
                setSavedWidgets(response.data.dashboard.widgets); // update backup
            }

            setIsEditing(false);
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to save dashboard');
        }
    };

    const startEditing = () => {
        setSavedWidgets(widgets); // backup current state
        setIsEditing(true);
    };

    useEffect(() => {
        if (!gridRef.current) {
            return;
        }

        // Init GridStack
        grid.current = GridStack.init(
            {
                column: 12,     // grid spaces per row
                cellHeight: 100,
                float: false,
                resizable: { handles: 'all' },
                draggable: { handle: '.widget-header' }
            },
            gridRef.current
        );

        // Listen for layout changes
        grid.current.on('change', (_, items) => {
            if (!items) {
                return;
            }

            // Extract ONLY the serializable layout values
            const clean = items.map(i => ({
                id: i.id,
                x: i.x,
                y: i.y,
                w: i.w,
                h: i.h
            }));

            setWidgets(prev =>
                prev.map(w => {
                    const updated = clean.find(i => i.id === w.id);
                    if (!updated) {
                        return w;
                    }

                    return {
                        ...w,
                        graphical: {
                            ...w.graphical,
                            position: { x: updated.x, y: updated.y },
                            size: { w: updated.w, h: updated.h }
                        }
                    };
                })
            );
        });

        return () => grid.current?.destroy(false);
    }, [widgets.length]);

    useEffect(() => {
        if (!grid.current) {
            return;
        }

        if (isEditing) {
            grid.current.setStatic(false);
            grid.current.enableMove(true);
            grid.current.enableResize(true);

            // Inject + widget into GridStack properly
            if (!plusWidgetAdded) {
                // 1. Create real DOM node
                const wrapper = document.createElement('div');
                wrapper.className = 'grid-stack-item';
                wrapper.style.padding = '8px';

                const inner = document.createElement('div');
                inner.className = `
                    tw-flex tw-items-center tw-justify-center
                    tw-border tw-border-dashed tw-border-gray-400
                    tw-rounded-xl tw-h-full tw-bg-gray-100 hover:tw-bg-gray-200
                    tw-cursor-pointer tw-transition
                `;

                const span = document.createElement('span');
                span.className = 'tw-text-4xl tw-text-gray-500';
                span.innerText = '+';

                inner.appendChild(span);
                wrapper.appendChild(inner);

                grid.current.addWidget({
                    id: 'add-widget',
                    x: 0,
                    y: 9999, // forces it to the bottom
                    w: 3,
                    h: 2,
                    el: wrapper
                });

                inner.addEventListener('click', addNewWidget);

                setPlusWidgetAdded(true);
            }
        } else {
            // Disable editing
            grid.current.setStatic(true);
            grid.current.enableMove(false);
            grid.current.enableResize(false);

            // Remove the + widget
            const el = gridRef.current.querySelector('[gs-id="add-widget"]');
            if (el) {
                grid.current.removeWidget(el);
            }

            setPlusWidgetAdded(false);
        }
    }, [isEditing]);

    return (
        <Background>
            <Container variant="full">
                <ContentArea>
                    {wizard && (
                        <WizardContainer>
                            <Wizard
                                handleSubmit={handleSubmit}
                                initialData={wizard.widget}
                                onCancel={closeWizard}
                                onClose={closeWizard}
                                onDataChange={onWizardDataChange}
                                steps={steps}
                            />
                        </WizardContainer>
                    )}
                    <ContentHeader icon="globe" title={`Dashboard ${WiseMetering.currentOrganization.get('name')}`}>
                        { WiseMetering.Permissions.hasRolePermissions('customDashboard', 'update') && (
                            isEditing ? (
                                <>
                                    <Button className="tw-mr-2" onClick={addNewWidget} size="sm" variant="secondary">
                                        { i18next.t('dashboard.custom_dashboard.new_widget') }
                                    </Button>
                                    <Button onClick={saveDashboard} size="sm" variant="default">
                                        { i18next.t('ui.buttons.save') }
                                    </Button>
                                    <Button className="tw-ml-2" onClick={cancelEditing} size="sm" variant="destructive">
                                        { i18next.t('ui.buttons.cancel') }
                                    </Button>
                                </>
                            ) : (
                                <Button onClick={startEditing} size="sm" variant="default">
                                    { i18next.t('ui.buttons.edit') }
                                </Button>
                            )
                        )}
                    </ContentHeader>
                    <div
                        className={cn(
                            'grid-stack tw-p-4 tw-gap-4 tw-min-h-[600px]',
                            isEditing && 'tw-bg-[linear-gradient(#ddd_1px,transparent_1px),linear-gradient(90deg,#ddd_1px,transparent_1px)] tw-bg-[size:calc(100%/12)_100px] tw-border-2 tw-border-solid tw-border-gray-300'
                        )}
                        ref={gridRef}
                    >
                        {widgets.map(widget => (
                            !widget.deleted && (
                                <div
                                    className="grid-stack-item relative"
                                    gs-h={widget.graphical.size.h}
                                    gs-id={widget.id}
                                    gs-min-h={widgetTypes[widget.type]?.minSize?.h}
                                    gs-min-w={widgetTypes[widget.type]?.minSize?.w}
                                    gs-w={widget.graphical.size.w}
                                    gs-x={widget.graphical.position?.x ?? undefined}
                                    gs-y={widget.graphical.position?.y ?? undefined}
                                    key={widget.id}
                                >
                                    <div className="tw-relative tw-h-full tw-w-full">
                                        {/* Widget content */}
                                        <Widget widget={widget}/>

                                        {/* Centered buttons only in editing mode */}
                                        {isEditing && (
                                            <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-gap-2 pointer-events-auto">
                                                <Button
                                                    aria-label={i18next.t('ui.buttons.edit')}
                                                    className={EDIT_BUTTON_BORDER}
                                                    onClick={() => editWidget(widget)}
                                                    size="icon"
                                                    title={i18next.t('ui.buttons.edit')}
                                                    variant="default"
                                                >
                                                    <IconFa color="white" icon="edit" size="small"/>
                                                </Button>
                                                <Button
                                                    aria-label={i18next.t('ui.buttons.remove')}
                                                    className={EDIT_BUTTON_BORDER}
                                                    onClick={() => removeWidget(widget.id)}
                                                    size="icon"
                                                    title={i18next.t('ui.buttons.remove')}
                                                    variant="destructive"
                                                >
                                                    <IconFa color="white" icon="delete" size="small"/>
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        ))}
                    </div>
                </ContentArea>
            </Container>
        </Background>
    );
}
